import { useStore } from '@/state/store';
import { Modal } from '@/components/ui/Modal';
import { TOOLS, TOOL_ORDER } from '@/tools/registry';
import { saveTool } from '@/state/persistence';
import type { ToolId } from '@/types';
import { cn } from '@/lib/utils';
import { ToolArt } from './ToolArt';

function Cards({ onPick, stagger = 0 }: { onPick: (tool: ToolId) => void; stagger?: number }) {
  return (
    <div className="grid gap-2.5 p-4 sm:grid-cols-3">
      {TOOL_ORDER.map((id, i) => {
        const tool = TOOLS[id];
        return (
          <button
            key={id}
            onClick={() => onPick(id)}
            style={stagger ? { animationDelay: `${stagger + i * 90}ms` } : undefined}
            className={cn(
              'group flex flex-col items-start gap-2 rounded-[12px] p-3.5 text-left',
              'transition-[transform,box-shadow,background-color] duration-200 [transition-timing-function:var(--ease)]',
              'ring-1 ring-[var(--hairline)] hover:bg-[var(--bg-hover)]',
              'hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)] hover:ring-[var(--accent)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
              stagger && 'animate-rise',
            )}
          >
            {/* The art scales as a whole on hover, which leaves the looping
                animations inside it untouched — restarting them on hover
                would read as a glitch. */}
            <span className="origin-left transition-transform duration-300 [transition-timing-function:var(--ease)] group-hover:scale-[1.06]">
              <ToolArt tool={id} />
            </span>
            <span className="text-[13.5px] font-semibold tracking-[-0.01em]">{tool.label}</span>
            <span className="text-[12px] leading-[1.45] text-[var(--text-secondary)]">
              {tool.blurb}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Shown full-screen on a first visit — when nothing is saved for any tool —
 * and as a modal from the "New" button. Picking from the modal starts that
 * tool over; picking on a first visit only chooses which one to open.
 */
export function ToolChooser() {
  const mode = useStore((s) => s.ui.chooserOpen);
  const setUi = useStore((s) => s.setUi);
  const setTool = useStore((s) => s.setTool);
  const newDoc = useStore((s) => s.newDoc);

  if (!mode) return null;

  const pick = (id: ToolId) => {
    if (mode === 'modal') newDoc(id);
    setTool(id);
    saveTool(id);
    history.replaceState(null, '', TOOLS[id].path);
    setUi({ chooserOpen: false, tab: TOOLS[id].defaultTab });
  };

  if (mode === 'modal') {
    return (
      <Modal
        open
        onClose={() => setUi({ chooserOpen: false })}
        title="Start something new"
        subtitle="Each tool keeps its own document — starting one over leaves the others untouched."
        width={720}
      >
        <Cards onPick={pick} />
      </Modal>
    );
  }

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center overflow-hidden bg-[var(--bg-app)] p-4">
      <AmbientBackdrop />

      <div className="relative w-full max-w-[720px]">
        <div className="px-4 pb-1 text-center">
          <h2 className="animate-rise text-[19px] font-semibold tracking-[-0.02em]">
            What do you want to make?
          </h2>
          <p
            className="animate-rise mt-1 text-[13px] text-[var(--text-secondary)]"
            style={{ animationDelay: '90ms' }}
          >
            Everything runs in your browser — your photos never leave this device.
          </p>
        </div>
        <Cards onPick={pick} stagger={180} />
      </div>
    </div>
  );
}

/**
 * A hairline grid drifting behind the cards — the shape all three tools have
 * in common, and the one background that cannot band: it is crisp lines
 * rather than a wide, faint wash, which quantises into visible rings on an
 * 8-bit display. One loop moves it exactly one cell, so it never restarts
 * visibly. Decoration only: under everything, and deaf to the pointer.
 */
function AmbientBackdrop() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
      style={{
        // Fades out towards the edges so the grid reads as texture, not as a
        // frame around the page.
        maskImage: 'radial-gradient(ellipse 52% 48% at 50% 46%, #000 0%, transparent 72%)',
        WebkitMaskImage:
          'radial-gradient(ellipse 52% 48% at 50% 46%, #000 0%, transparent 72%)',
      }}
    >
      <span
        className="absolute -inset-[40px] opacity-[0.55]"
        style={{
          backgroundImage:
            'linear-gradient(var(--hairline) 1px, transparent 1px), linear-gradient(90deg, var(--hairline) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          animation: 'grid-drift 24s linear infinite',
        }}
      />
    </div>
  );
}
